import { AvailabilityTimeFrame, type AvailabilityRule } from '@kouto/types';
import { RadixSelect } from 'components/common/RadixSelect';
import { StayRangePicker } from 'components/molecules/StayRangePicker';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import { format, parseISO } from 'date-fns';
import {
  isDayBasedTimeFrame,
  type ContinuousSelection,
} from 'utils/continuousSelection';
import { MINUTES_PER_DAY } from 'utils/date/minutesPerDay';
import { formatTimeShort } from 'utils/dateTime';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { BottomSheetTimePicker } from 'components/molecules/BottomDrawer/BottomSheetTimePicker';
import useIsMobile from 'hooks/useIsMobile';
import SpecificDurationOptions from './SpecificDurationOptions';

/** The element the embed's styles are scoped to; see `index.js`, which namespaces every rule under it. */
const EMBED_ROOT_ID = 'kouto-embed-root';

interface ContinuousSessionPickerProps {
  /** Dates with a bookable span on some resource, for the day-based calendar; "YYYY-MM-DD". */
  availableDates?: readonly string[];
  date: string;
  /** The lengths offered from the chosen start, in minutes; from `useContinuousOptions`. */
  durationOptions: readonly number[];
  isLoadingAvailableDates?: boolean;
  /** A length picked from `durationOptions`. */
  onDurationSelect: (duration: number) => void;
  /** The day-based calendar has moved months, so the parent widens what it knows is available. */
  onMonthChange?: (month: Date) => void;
  /** A start date and a length picked together on the day-based calendar. */
  onRangeChange?: (date: string, duration: number) => void;
  /** A whole stay in minutes, when the day-based calendar has no `onRangeChange` to send it to. */
  onStayChange?: (duration: number) => void;
  /** A start picked from `startOptions`; the parent decides what length goes with it. */
  onStartSelect: (time: string) => void;
  rule: AvailabilityRule;
  selection: ContinuousSelection | null;
  /** The starts offered on this date, "HH:mm:ss"; from `useContinuousOptions`. */
  startOptions: readonly string[];
  timeFrame: AvailabilityTimeFrame;
}

/**
 * How a guest chooses a continuously booked resource: a start time and a length for one booked by the
 * hour, a count of days or nights for one booked by the day. Every option offered is free on the date
 * shown, so a choice here is always one the booking will accept.
 */
/** Minutes into the day of "HH:mm:ss". */
const toMinutesOfDay = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/** "HH:mm:ss" for minutes into a day, wrapping past midnight. */
const toTimeOfDay = (minutes: number): string => {
  const wrapped = minutes % MINUTES_PER_DAY;
  return `${`${Math.floor(wrapped / 60)}`.padStart(2, '0')}:${`${
    wrapped % 60
  }`.padStart(2, '0')}:00`;
};

const ContinuousSessionPicker = ({
  availableDates = [],
  date,
  durationOptions,
  isLoadingAvailableDates = false,
  onDurationSelect,
  onMonthChange,
  onRangeChange,
  onStartSelect,
  onStayChange,
  rule,
  selection,
  startOptions,
  timeFrame,
}: ContinuousSessionPickerProps) => {
  const { t: translate } = useTranslation();
  const { locale } = useGlobalDateLocale();
  const isDayBased = isDayBasedTimeFrame(timeFrame);
  const isMobile = useIsMobile();
  // A Radix menu is portalled to document.body by default, which is outside the embed's root, so none
  // of its styles — background, stacking — would reach it. It is portalled into the root instead.
  const portalContainer = useMemo(
    () => document.getElementById(EMBED_ROOT_ID),
    [],
  );

  const selectedTime = selection?.startDateTime.split('T')[1];
  const selectedDuration = selection?.durationMinutes;

  // The group speaks in strings; lengths travel as minutes.
  const handleDurationOptionSelect = (value: string) =>
    onDurationSelect(Number(value));

  const handleRangeChange = (nextDate: string, units: number) => {
    const duration = units * MINUTES_PER_DAY;
    if (onRangeChange) {
      onRangeChange(nextDate, duration);
      return;
    }
    onStayChange?.(duration);
  };

  if (isDayBased) {
    return (
      <Section
        className="continuous-session-picker continuous-session-picker--day-based"
        data-testid="continuous-session-picker"
      >
        <StayRangePicker
          availableDates={availableDates}
          date={date}
          isLoadingAvailableDates={isLoadingAvailableDates}
          onMonthChange={onMonthChange ?? (() => undefined)}
          onRangeChange={handleRangeChange}
          rule={rule}
          timeFrame={timeFrame}
          units={selection?.units}
        />
        {selection && (
          <Hint
            className="continuous-session-picker-hint"
            data-testid="continuous-session-picker-hint"
          >
            <span className="continuous-session-picker-hint-start">
              {translate('pickupFrom')}{' '}
              {formatTimeShort(selection.startDateTime.split('T')[1], locale)}
            </span>{' '}
            ·{' '}
            <span className="continuous-session-picker-hint-end">
              {translate('returnBy')}{' '}
              {format(
                new Date(
                  parseISO(selection.startDateTime).getTime() +
                    selection.durationMinutes * 60_000,
                ),
                'p',
                { locale },
              )}
            </span>
          </Hint>
        )}
      </Section>
    );
  }

  // The lengths on offer from the chosen start are the end times a guest can pick, so each end maps
  // back to a length.
  const startMinutes = selectedTime ? toMinutesOfDay(selectedTime) : null;
  const endOptions =
    startMinutes === null
      ? []
      : durationOptions.map((duration) => {
          const endTime = toTimeOfDay(startMinutes + duration);
          return {
            label: formatTimeShort(endTime, locale),
            timeOfDay: endTime,
            value: String(duration),
          };
        });

  // A phone gets the same two fields for a flexible resource and one sold in set lengths: each opens a
  // drawer of buttons, saved with Save.
  if (isMobile) {
    return (
      <Section
        className="continuous-session-picker continuous-session-picker--mobile"
        data-testid="continuous-session-picker"
      >
        <Fields>
          <Field>
            <Label
              className="continuous-session-picker__label"
              data-testid="start-time-label"
            >
              {translate('startTime')}
            </Label>
            <BottomSheetTimePicker
              disabled={startOptions.length === 0}
              heading={translate('startTime')}
              onSave={onStartSelect}
              options={startOptions.map((time) => ({
                label: formatTimeShort(time, locale),
                timeOfDay: time,
                value: time,
              }))}
              placeholder={translate('selectStartTime')}
              selectedValue={selectedTime}
              testId="start-time-drawer"
            />
          </Field>
          <Field>
            <Label
              className="continuous-session-picker__label"
              data-testid="end-time-label"
            >
              {translate('endTime')}
            </Label>
            <BottomSheetTimePicker
              disabled={endOptions.length === 0}
              heading={translate('endTime')}
              onSave={handleDurationOptionSelect}
              options={endOptions}
              placeholder={translate('selectEndTime')}
              selectedValue={
                selectedDuration !== undefined
                  ? String(selectedDuration)
                  : undefined
              }
              testId="end-time-drawer"
            />
          </Field>
        </Fields>
      </Section>
    );
  }

  // A resource sold in set lengths is every start as a button, then the lengths as buttons.
  if (timeFrame === AvailabilityTimeFrame.SPECIFIC_DURATIONS) {
    return (
      <Section
        className="continuous-session-picker continuous-session-picker--specific"
        data-testid="continuous-session-picker"
      >
        <SpecificDurationOptions
          durationOptions={durationOptions}
          onDurationSelect={onDurationSelect}
          onStartSelect={onStartSelect}
          selectedDuration={selectedDuration}
          selectedTime={selectedTime}
          startOptions={startOptions}
        />
      </Section>
    );
  }

  // A start time and an end time, each a dropdown side by side.
  return (
    <Section
      className="continuous-session-picker continuous-session-picker--flexible"
      data-testid="continuous-session-picker"
    >
      <Fields>
        <Field>
          <Label
            className="continuous-session-picker__label"
            data-testid="start-time-label"
          >
            {translate('startTime')}
          </Label>
          <RadixSelect
            aria-label={translate('startTime')}
            disabled={startOptions.length === 0}
            onValueChange={onStartSelect}
            options={startOptions.map((time) => ({
              label: formatTimeShort(time, locale),
              value: time,
            }))}
            placeholder={translate('selectStartTime')}
            portalContainer={portalContainer}
            value={selectedTime ?? ''}
          />
        </Field>
        <Field>
          <Label
            className="continuous-session-picker__label"
            data-testid="end-time-label"
          >
            {translate('endTime')}
          </Label>
          <RadixSelect
            aria-label={translate('endTime')}
            disabled={endOptions.length === 0}
            onValueChange={handleDurationOptionSelect}
            options={endOptions}
            placeholder={translate('selectEndTime')}
            portalContainer={portalContainer}
            value={
              selectedDuration !== undefined ? String(selectedDuration) : ''
            }
          />
        </Field>
      </Fields>
    </Section>
  );
};

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const Fields = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  width: 100%;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;

  /* Two fields share a narrow row, so a trigger may shrink below the drawer trigger's usual width. */
  & button {
    min-width: 0;
  }
`;

const Label = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
  color: var(--way-colors-contrastColorShades-80);
`;

const Hint = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: var(--way-colors-contrastColorShades-70);
`;

export default ContinuousSessionPicker;
